/* eslint-disable @typescript-eslint/no-explicit-any */
import DynamicModal from 'components/DynamicModal';
import React, { useState } from 'react';
import CADetailModal from 'views/Admin/Dashboard/CADashboard/CAList/CADetailModal';
import EPDetailModal from 'views/Admin/Dashboard/EPDashboard/EPList/EPDetailModal';
import WADetailModal from 'views/Admin/Dashboard/WADashboard/WAList/WADetailModal';
import RepositoryDetailModalContent from 'views/sast2/Repositories/RepositoryDetailModal/RepositoryDetailModalContent';

import { SourceType } from '../state';

/**
 * Configuration for detail modals based on source type
 */
const detailModalConfig = [
  {
    type: 'WA',
    sourceTypes: [SourceType.WAS],
    idField: 'webapp_id',
    title: 'Web Application Details',
    component: (props) => <WADetailModal {...props} />,
  },
  {
    type: 'EP',
    sourceTypes: [
      SourceType.EP,
      SourceType.EP_EXTERNAL,
      SourceType.EP_INTERNAL,
      SourceType.EP_EC2,
    ],
    idField: 'endpoint_id',
    title: 'Endpoint Details',
    component: (props) => <EPDetailModal {...props} />,
  },
  {
    type: 'CA',
    sourceTypes: [
      SourceType.CA,
      SourceType.CA_SERVER,
      SourceType.CA_WORKSTATIONS,
    ],
    idField: 'cloud_agent_id',
    title: 'Cloud Agent Details',
    component: (props) => <CADetailModal {...props} />,
  },
  {
    type: 'SAST',
    sourceTypes: [SourceType.SAST],
    idField: 'repository_id',
    title: 'Repository Details',
    component: (props) => <RepositoryDetailModalContent id={props.id} />,
  },
];

/**
 * Hook for managing detail modals
 * @returns Object with openModal and renderModal functions
 */
export const useDetailModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<any>(null);
  const [modalId, setModalId] = useState<number | null>(null);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const openModal = (rowData: any) => {
    if (!rowData) return;

    // Find the matching modal configuration based on source type
    const config = detailModalConfig.find((config) =>
      config.sourceTypes.includes(rowData.source_type),
    );

    if (config && rowData[config.idField]) {
      setModalId(rowData[config.idField]);
      setModalConfig(config);
      setIsModalOpen(true);
    }
  };

  const renderModal = () => {
    if (!isModalOpen || !modalConfig || modalId === null) return null;

    const modalProps = {
      isOpen: isModalOpen,
      toggle: toggleModal,
      title: modalConfig.title,
    };

    // Render the modal with the configured component
    return (
      <DynamicModal
        {...modalProps}
        content={modalConfig.component({
          id: modalId,
          isOpen: isModalOpen,
          toggle: toggleModal,
        })}
      />
    );
  };

  return { openModal, renderModal };
};

/**
 * Component for rendering a clickable host with a detail modal
 */
interface HostDetailModalsProps {
  rowData: any;
  hostNameRenderer: (rowData: any) => React.ReactNode;
  icon: React.ReactNode;
}

export const HostDetailModals: React.FC<HostDetailModalsProps> = ({
  rowData,
  hostNameRenderer,
  icon,
}) => {
  const { openModal, renderModal } = useDetailModal();

  const handleClick = () => {
    openModal(rowData);
  };

  return (
    <>
      <div
        className="text-nowrap"
        style={{ cursor: 'pointer', textDecoration: 'underline' }}
        onClick={handleClick}
      >
        {icon} {hostNameRenderer(rowData)}
      </div>
      {renderModal()}
    </>
  );
};
